// Skill Creator System
// Players design their own skills by selecting a base type and adjusting parameters
// Trade-off system: increasing one parameter affects others

import { ElementType } from '@shared/types';
import { SkillTypeId, SKILL_TYPES } from './SkillTypes';
import { CustomSkillV2, SkillCreatorV2 } from './SkillCreatorV2';

// Custom skill created by player
export interface CustomSkill {
  id: string;
  name: string;
  baseType: SkillTypeId;
  element: ElementType;

  // Customized parameters (0-100 slider values)
  sliders: {
    damage: number;      // 0-100, affects damage multiplier
    area: number;        // 0-100, affects AoE radius
    speed: number;       // 0-100, affects cast speed
    projectiles: number; // 0-100, affects projectile count
    range: number;       // 0-100, affects range
  };

  // Calculated costs (based on slider trade-offs)
  manaCost: number;
  cooldown: number;

  // Socket slots for effect stones
  socketCount: number;
  sockets: (EffectStone | null)[];

  // Runtime state
  currentCooldown: number;
  level: number;
  xp: number;       // Current skill XP
  maxXp: number;    // XP needed for next level
}

// Effect stone for skill modification
export interface EffectStone {
  id: string;
  name: string;
  nameKey: string;
  type: 'od' | 'gok' | 'su' | 'karanlik';
  rarity: 'normal' | 'magic' | 'rare' | 'legendary';
  effect: EffectStoneEffect;
}

export interface EffectStoneEffect {
  id: string;
  name: string;
  description: string;
  // Stat modifiers
  damageBonus?: number;      // % bonus
  areaBonus?: number;        // % bonus
  cooldownReduction?: number; // % reduction
  manaCostReduction?: number; // % reduction
  // Special effects
  elementOverride?: ElementType;
  chainCount?: number;       // Chain to X more enemies
  lifesteal?: number;        // % of damage as life
  slow?: number;             // % slow on hit
  knockback?: number;        // Knockback distance
  dot?: { damage: number; duration: number }; // Damage over time
}

// Trade-off ratios: how much cost increases when you boost a stat
const TRADE_OFF_RATIOS = {
  damage: {
    manaCostMult: 1.5,    // +50% mana per 50 damage slider
    cooldownMult: 1.2,    // +20% cooldown per 50 damage slider
  },
  area: {
    manaCostMult: 1.8,    // +80% mana per 50 area slider
    damageMult: 0.8,      // -20% damage per 50 area slider (trade-off!)
  },
  speed: {
    manaCostMult: 1.3,    // +30% mana per 50 speed slider
    cooldownMult: 1.4,    // +40% cooldown per 50 speed slider
  },
  projectiles: {
    manaCostMult: 2.0,    // +100% mana per 50 projectile slider
    damagePerProjectileMult: 0.7, // Each projectile does less damage
  },
  range: {
    manaCostMult: 1.2,    // +20% mana per 50 range slider
  },
};

// Skill Creator class
export class SkillCreator {
  // Create a new skill from base type with default sliders
  static createSkill(
    baseType: SkillTypeId,
    name: string,
    element?: ElementType
  ): CustomSkill {
    const typeDef = SKILL_TYPES[baseType];
    if (!typeDef) {
      throw new Error(`Unknown skill type: ${baseType}`);
    }

    const skill: CustomSkill = {
      id: `skill_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      name,
      baseType,
      element: element || typeDef.defaultElement,
      sliders: {
        damage: 50,
        area: 50,
        speed: 50,
        projectiles: 50,
        range: 50,
      },
      manaCost: typeDef.baseManaCost,
      cooldown: typeDef.baseCooldown,
      socketCount: 2,
      sockets: [null, null],
      currentCooldown: 0,
      level: 1,
      xp: 0,
      maxXp: 10,
    };

    // Calculate initial costs
    this.recalculateCosts(skill);

    return skill;
  }

  /**
   * Add XP to a skill. Returns true if the skill leveled up.
   */
  static addXP(skill: CustomSkill, amount: number): boolean {
    skill.xp += amount;
    let didLevelUp = false;
    while (skill.xp >= skill.maxXp) {
      skill.xp -= skill.maxXp;
      skill.level++;
      // XP requirement grows: 10, 15, 22, 33, 50, ...
      skill.maxXp = Math.floor(10 * Math.pow(1.5, skill.level - 1));
      didLevelUp = true;
    }
    return didLevelUp;
  }

  // Adjust a slider and recalculate costs
  static adjustSlider(
    skill: CustomSkill,
    slider: keyof CustomSkill['sliders'],
    value: number
  ): void {
    skill.sliders[slider] = Math.max(0, Math.min(100, value));
    this.recalculateCosts(skill);
  }

  // Recalculate mana cost and cooldown based on sliders
  static recalculateCosts(skill: CustomSkill): void {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return;

    let manaMult = 1.0;
    let cooldownMult = 1.0;

    // Apply trade-offs based on slider positions
    // Sliders at 50 = default, >50 = boost with cost, <50 = reduction with benefit

    // Damage slider
    const damageOffset = (skill.sliders.damage - 50) / 50;
    manaMult *= 1 + damageOffset * (TRADE_OFF_RATIOS.damage.manaCostMult - 1);
    cooldownMult *= 1 + damageOffset * (TRADE_OFF_RATIOS.damage.cooldownMult - 1);

    // Area slider
    const areaOffset = (skill.sliders.area - 50) / 50;
    manaMult *= 1 + areaOffset * (TRADE_OFF_RATIOS.area.manaCostMult - 1);

    // Speed slider
    const speedOffset = (skill.sliders.speed - 50) / 50;
    manaMult *= 1 + speedOffset * (TRADE_OFF_RATIOS.speed.manaCostMult - 1);
    cooldownMult *= 1 + speedOffset * (TRADE_OFF_RATIOS.speed.cooldownMult - 1);

    // Projectiles slider
    const projOffset = (skill.sliders.projectiles - 50) / 50;
    manaMult *= 1 + projOffset * (TRADE_OFF_RATIOS.projectiles.manaCostMult - 1);

    // Range slider
    const rangeOffset = (skill.sliders.range - 50) / 50;
    manaMult *= 1 + rangeOffset * (TRADE_OFF_RATIOS.range.manaCostMult - 1);

    // Apply effect stone modifiers
    for (const stone of skill.sockets) {
      if (stone?.effect.cooldownReduction) {
        cooldownMult *= 1 - stone.effect.cooldownReduction / 100;
      }
      if (stone?.effect.manaCostReduction) {
        manaMult *= 1 - stone.effect.manaCostReduction / 100;
      }
    }

    // Calculate final values
    skill.manaCost = Math.max(1, Math.floor(typeDef.baseManaCost * manaMult));
    skill.cooldown = Math.max(0.1, typeDef.baseCooldown * cooldownMult);
  }

  // Get effective damage multiplier
  static getEffectiveDamage(skill: CustomSkill): number {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return 1;

    const { min, max } = typeDef.baseParams.damage;
    const sliderNorm = skill.sliders.damage / 100;
    let damage = min + (max - min) * sliderNorm;

    // Area trade-off: more area = less damage
    const areaOffset = (skill.sliders.area - 50) / 50;
    damage *= 1 + areaOffset * (TRADE_OFF_RATIOS.area.damageMult - 1);

    // Projectile trade-off: more projectiles = less damage per projectile
    const projCount = this.getEffectiveProjectileCount(skill);
    if (projCount > 1) {
      const projOffset = (skill.sliders.projectiles - 50) / 50;
      damage *= 1 + projOffset * (TRADE_OFF_RATIOS.projectiles.damagePerProjectileMult - 1);
    }

    // Level scaling
    damage *= 1 + (skill.level - 1) * 0.1;

    // Effect stone bonuses
    for (const stone of skill.sockets) {
      if (stone?.effect.damageBonus) {
        damage *= 1 + stone.effect.damageBonus / 100;
      }
    }

    return damage;
  }

  // Get effective area radius
  static getEffectiveArea(skill: CustomSkill): number {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return 0;

    const { min, max } = typeDef.baseParams.areaRadius;
    if (max === 0) return 0; // Single target skill

    const sliderNorm = skill.sliders.area / 100;
    let area = min + (max - min) * sliderNorm;

    // Effect stone bonuses
    for (const stone of skill.sockets) {
      if (stone?.effect.areaBonus) {
        area *= 1 + stone.effect.areaBonus / 100;
      }
    }

    return area;
  }

  // Get effective range
  static getEffectiveRange(skill: CustomSkill): number {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return 0;

    const { min, max } = typeDef.baseParams.range;
    const sliderNorm = skill.sliders.range / 100;
    return min + (max - min) * sliderNorm;
  }

  // Get effective cast speed
  static getEffectiveCastSpeed(skill: CustomSkill): number {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return 1;

    const { min, max } = typeDef.baseParams.castSpeed;
    const sliderNorm = skill.sliders.speed / 100;
    return min + (max - min) * sliderNorm;
  }

  // Get effective projectile count
  static getEffectiveProjectileCount(skill: CustomSkill): number {
    const typeDef = SKILL_TYPES[skill.baseType];
    if (!typeDef) return 1;

    const { min, max } = typeDef.baseParams.projectileCount;
    if (max === 1) return 1; // Single projectile skill

    const sliderNorm = skill.sliders.projectiles / 100;
    return Math.floor(min + (max - min) * sliderNorm);
  }

  // Get effective element (can be overridden by stones)
  static getEffectiveElement(skill: CustomSkill): ElementType {
    for (const stone of skill.sockets) {
      if (stone?.effect.elementOverride) {
        return stone.effect.elementOverride;
      }
    }
    return skill.element;
  }

  // Socket an effect stone
  static socketStone(skill: CustomSkill, slotIndex: number, stone: EffectStone): EffectStone | null {
    if (slotIndex < 0 || slotIndex >= skill.socketCount) return null;

    const previous = skill.sockets[slotIndex];
    skill.sockets[slotIndex] = stone;
    this.recalculateCosts(skill);
    return previous;
  }

  // Remove a socketed stone
  static unsocketStone(skill: CustomSkill, slotIndex: number): EffectStone | null {
    if (slotIndex < 0 || slotIndex >= skill.socketCount) return null;

    const stone = skill.sockets[slotIndex];
    skill.sockets[slotIndex] = null;
    this.recalculateCosts(skill);
    return stone;
  }

  // Check if skill is ready
  static isReady(skill: CustomSkill): boolean {
    return skill.currentCooldown <= 0;
  }

  // Check if can use with current mana
  static canUse(skill: CustomSkill, currentMana: number): boolean {
    return this.isReady(skill) && currentMana >= skill.manaCost;
  }

  // Use the skill
  static use(skill: CustomSkill, cooldownMultiplier: number = 1.0): void {
    skill.currentCooldown = skill.cooldown * cooldownMultiplier;
  }

  // Update cooldown
  static update(skill: CustomSkill, deltaTime: number): void {
    if (skill.currentCooldown > 0) {
      skill.currentCooldown = Math.max(0, skill.currentCooldown - deltaTime);
    }
  }

  // Get cooldown percent (0-1)
  static getCooldownPercent(skill: CustomSkill): number {
    if (skill.currentCooldown <= 0) return 0;
    return skill.currentCooldown / skill.cooldown;
  }

  // Serialize skill for saving
  static serialize(skill: CustomSkill): object {
    return {
      id: skill.id,
      name: skill.name,
      baseType: skill.baseType,
      element: skill.element,
      sliders: { ...skill.sliders },
      level: skill.level,
      xp: skill.xp || 0,
      maxXp: skill.maxXp || 10,
      sockets: skill.sockets.map(s => s ? s.id : null),
    };
  }

  // Deserialize skill from saved data
  static deserialize(data: Record<string, unknown>): CustomSkill | null {
    if (!data || !data.baseType) return null;

    const baseType = data.baseType as SkillTypeId;
    const name = (data.name as string) || 'Skill';
    const element = data.element as ElementType | undefined;

    try {
      const skill = this.createSkill(baseType, name, element);

      // Restore saved properties
      skill.id = (data.id as string) || skill.id;
      skill.level = (data.level as number) || 1;
      skill.xp = (data.xp as number) || 0;
      skill.maxXp = (data.maxXp as number) || Math.floor(10 * Math.pow(1.5, skill.level - 1));

      // Restore slider values
      if (data.sliders && typeof data.sliders === 'object') {
        const savedSliders = data.sliders as Record<string, number>;
        for (const key of Object.keys(skill.sliders) as (keyof CustomSkill['sliders'])[]) {
          if (key in savedSliders && typeof savedSliders[key] === 'number') {
            skill.sliders[key] = savedSliders[key];
          }
        }
      }

      // Recalculate costs based on restored sliders
      this.recalculateCosts(skill);

      return skill;
    } catch {
      return null;
    }
  }

  // Clone a skill
  static clone(skill: CustomSkill): CustomSkill {
    return {
      ...skill,
      id: `skill_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      sliders: { ...skill.sliders },
      sockets: [...skill.sockets],
      currentCooldown: 0,
    };
  }
}

// Combined skill type for hotbar (supports both V1 and V2)
export type HotbarSkill = CustomSkill | CustomSkillV2;

// Helper to check if skill is V2
export function isSkillV2(skill: HotbarSkill): skill is CustomSkillV2 {
  return 'version' in skill && skill.version === 2;
}

// Skill hotbar manager - supports both V1 and V2 skills
export class SkillHotbar {
  public slots: (HotbarSkill | null)[] = [null, null, null, null, null, null];
  public maxSlots: number = 6;

  // Assign skill to slot (V1 or V2)
  assign(skill: HotbarSkill, slotIndex: number): boolean {
    if (slotIndex < 0 || slotIndex >= this.maxSlots) return false;

    // Remove from previous slot if already assigned
    for (let i = 0; i < this.slots.length; i++) {
      if (this.slots[i]?.id === skill.id) {
        this.slots[i] = null;
      }
    }

    this.slots[slotIndex] = skill;
    return true;
  }

  // Clear a slot
  clear(slotIndex: number): void {
    if (slotIndex >= 0 && slotIndex < this.maxSlots) {
      this.slots[slotIndex] = null;
    }
  }

  // Get skill at slot
  get(slotIndex: number): HotbarSkill | null {
    if (slotIndex < 0 || slotIndex >= this.maxSlots) return null;
    return this.slots[slotIndex];
  }

  // Get V1 skill at slot (for backward compatibility)
  getV1(slotIndex: number): CustomSkill | null {
    const skill = this.get(slotIndex);
    if (skill && !isSkillV2(skill)) {
      return skill;
    }
    return null;
  }

  // Get V2 skill at slot
  getV2(slotIndex: number): CustomSkillV2 | null {
    const skill = this.get(slotIndex);
    if (skill && isSkillV2(skill)) {
      return skill;
    }
    return null;
  }

  // Update all skills
  update(deltaTime: number): void {
    for (const skill of this.slots) {
      if (skill) {
        if (isSkillV2(skill)) {
          SkillCreatorV2.update(skill, deltaTime);
        } else {
          SkillCreator.update(skill, deltaTime);
        }
      }
    }
  }

  // Use skill at slot (returns info about the skill used)
  use(slotIndex: number, currentMana: number, cooldownMultiplier: number = 1.0, attackSpeedCooldown?: number): {
    success: boolean;
    manaCost: number;
    skill: HotbarSkill | null;
    isV2: boolean;
  } {
    const skill = this.get(slotIndex);
    if (!skill) {
      return { success: false, manaCost: 0, skill: null, isV2: false };
    }

    if (isSkillV2(skill)) {
      // V2 skill handling
      if (!SkillCreatorV2.canUse(skill, currentMana)) {
        return { success: false, manaCost: 0, skill, isV2: true };
      }

      const manaCost = SkillCreatorV2.use(skill, cooldownMultiplier, attackSpeedCooldown);
      return { success: true, manaCost, skill, isV2: true };
    } else {
      // V1 skill handling
      if (!SkillCreator.canUse(skill, currentMana)) {
        return { success: false, manaCost: 0, skill, isV2: false };
      }

      SkillCreator.use(skill, cooldownMultiplier);
      return { success: true, manaCost: skill.manaCost, skill, isV2: false };
    }
  }

  // Find slot index for a skill by ID
  findSlotBySkillId(skillId: string): number {
    return this.slots.findIndex(s => s?.id === skillId);
  }
}
